import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// KDA Fee Pool - for paying transaction fees with KLYX instead of KLV
export interface FeePoolData {
  kdaId: string;
  isActive: boolean;
  feeConversionRate: string; // e.g., "1 KLV = 2 KLYX"
  fRatioKLV: number;
  fRatioKDA: number;
  poolAddress: string;
  poolBalance: number;
  userBalance?: number; // User's deposited balance in fee pool
  timestamp: number;
}

// FPR Staking - for freezing tokens and earning proportional rewards
export interface StakingData {
  kdaId: string;
  isActive: boolean;
  totalStaked: number;
  currentFPRAmount: number; // Total rewards available to claim
  fprDeposits: Array<{
    amount: number;
    epoch: number;
  }>;
  minEpochsToClaim: number; // Usually 100 epochs
  minEpochsToUnstake: number;
  minEpochsToWithdraw: number;
  interestType: string; // "APRI" or "FPR"
  userStaked?: number; // User's staked amount
  userRewards?: number; // User's claimable rewards
  timestamp: number;
}

export interface PoolState {
  feePool: FeePoolData | null;
  staking: StakingData | null;
  userPositions: Array<{
    amount: number;
    depositTime: number;
    unlockTime: number;
    rewards: number;
  }>;
  isLoading: boolean;
  error: string | null; // Deprecated - kept for backward compatibility
  feePoolError: string | null;
  stakingError: string | null;
  txHash: string | null; // Last transaction hash
}

const initialState: PoolState = {
  feePool: null,
  staking: null,
  userPositions: [],
  isLoading: false,
  error: null,
  feePoolError: null,
  stakingError: null,
  txHash: null,
};

const poolSlice = createSlice({
  name: 'pool',
  initialState,
  reducers: {
    setFeePoolData: (state, action: PayloadAction<FeePoolData>) => {
      state.feePool = action.payload;
      state.feePoolError = null;
    },
    setStakingData: (state, action: PayloadAction<StakingData>) => {
      state.staking = action.payload;
      state.stakingError = null;
    },
    setUserPositions: (state, action: PayloadAction<PoolState['userPositions']>) => {
      state.userPositions = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    setFeePoolError: (state, action: PayloadAction<string>) => {
      state.feePoolError = action.payload;
      state.isLoading = false;
    },
    setStakingError: (state, action: PayloadAction<string>) => {
      state.stakingError = action.payload;
      state.isLoading = false;
    },
    setTxHash: (state, action: PayloadAction<string>) => {
      state.txHash = action.payload;
    },
    clearTxHash: (state) => {
      state.txHash = null;
    },
    clearError: (state) => {
      state.error = null;
      state.feePoolError = null;
      state.stakingError = null;
    },
    clearFeePoolError: (state) => {
      state.feePoolError = null;
    },
    clearStakingError: (state) => {
      state.stakingError = null;
    },
  },
});

export const {
  setFeePoolData,
  setStakingData,
  setUserPositions,
  setLoading,
  setError,
  setFeePoolError,
  setStakingError,
  setTxHash,
  clearTxHash,
  clearError,
  clearFeePoolError,
  clearStakingError,
} = poolSlice.actions;

export default poolSlice.reducer;
